import { APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { DoBootstrap } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { UpgradeModule, downgradeInjectable } from '@angular/upgrade/static';

import { UIRouterUpgradeModule } from '@uirouter/angular-hybrid';
import { UIRouter, UIRouterModule } from '@uirouter/angular';
import { provideProjectStatesInitializer } from './project-display/register-projectStates.initializer';
import { EICDataStream3Module } from './app.eicDataStream3.module';
import { EICDataStream3Config } from '../app-ds3/app.config.js';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { HTTP_INTERCEPTORS } from '@angular/common/http';

import { BffInterceptor } from './auth/bff-interceptor';
import { ApiInterceptor } from './auth/api-interceptor';
import { ErrorInterceptor } from './auth/error-interceptor';

import { PrefsModule } from './prefs/prefs.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';

import { MenuModule } from './menu/menu.module';

// A lazy-loaded module is not included in the imports
// import { ReportsModule } from './reports/reports.module';

import { DsCoreModule } from './dsCore/core.module';
import { UserGuideModule } from './user-guide/user-guide.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AppInitService } from './app-init.service';
import { CurrentUserService } from './shared/services/current-user.service';
import { CommonModule } from '@angular/common';
import { EventQueueService } from './shared/utils/AppEventType';
import { LastSearchService } from './shared/services/lastSearchService';
import { FunctionUnavailableComponent } from './shared/ui/function-unavailable/function-unavailable.component';
import { ProjectDisplayModule } from './project-display/project-display.module';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ThemeService } from './shared/services/theme.service';
import { SplashscreenService } from './shared/services/splashscreen.service';
import { ApiKeyManagerModule } from './api-key-manager/api-key-manager.module';
import { DashboardModule } from './dashboard/dashboard.module';
import { catchError, filter, firstValueFrom, lastValueFrom, of, switchMap, take, timeout } from 'rxjs';

import { FeaturesService } from './shared/services/features.service';
import { ConfigService } from './config/config.service';
import { applyAngularJsConfigFromRuntime } from './config/angularjs-config-adapter';
import { provideConfigSlices } from './config/provide-config-slices';
import { FEATURES_TOKEN } from './config/config.tokens';
import { registerProjectStates } from './project-display/project-display.states';
import { AssetAnalysisChartModule } from './assets/ui/asset-analysis-chart.module';
import { NotificationsModule } from './notifications/notifications.module';
import { registerNotificationStates } from './notifications/notifications.states';
import { GeographicalSummaryModule } from './geographical-summary/geographical-summary.module';
import { registerGeographicalSummaryStates } from './geographical-summary/geographical-summary.states';
import { ManagePdfReportRequestLauncherService } from './reports/data-access/manage-pdf-report-request-launcher.service';
import { AppIconRegistryService } from './shared/services/app-icon-registry.service';

// Type-only declaration for global AngularJS
declare const angular: angular.IAngularStatic;



export function configInitializer(cfg: ConfigService) {
  // Loads runtime configuration (JSON or env.js) before anything else uses it
  return () => cfg.load();
}

export function initializeEICDataStream(appInitService: AppInitService) { //, currentUser:NewCurrentUserService

  return () => appInitService.Init();

}


// export function initCurrentUserFactory(currentUser: CurrentUserService) {
//   return () => firstValueFrom(
//     currentUser.init().pipe(
//       filter(u => u !== null),
//       take(1),
//       // Optional: ensure bootstrap isn’t blocked forever
//       timeout(4000)
//     )
//   ).catch(() => void 0);
//}

// export function initCurrentUserFactory(currentUser: CurrentUserService) {
//   return () =>
//     firstValueFrom(
//       currentUser.init().pipe(
//         // Wait for the initial basic emission
//         take(1),
//         // If not authenticated, resolve immediately. If authenticated, wait for enrichment.
//         switchMap((u) => {
//           if (!u || !u.isAuthenticated) {
//             // Not authenticated (or no user) -> don't wait for enrichment
//             return of(u);
//           }

//           // Authenticated: wait for an emission that contains the `full` property
//           // (the service emits a second value with `full` set when enrichment completes;
//           // the initial basic user typically won't have a `full` property)
//           return currentUser.user$.pipe(
//             filter((v) => v != null && Object.prototype.hasOwnProperty.call(v, 'full')),
//             take(1),
//             // Don't block bootstrap forever — fall back after timeout
//             timeout(4000),
//             catchError(() => of(u)) // on timeout or error, fall back to the basic user
//           );
//         })
//       )
//     ).catch(() => void 0);
// }


export function initCurrentUserFactory(currentUser: CurrentUserService, cfg: ConfigService, splashscreen: SplashscreenService) {
  return async () => {
    await cfg.whenReady();
    splashscreen.configure(cfg.splashscreen(), !!cfg.dashboard().enableNewDashboard);

    // original logic preserved
    await firstValueFrom(
      currentUser.init().pipe(
        take(1),
        switchMap((u) => {
          if (!u || !u.isAuthenticated) {
            return of(u);
          }
          return currentUser.user$.pipe(
            filter((v) => v != null && Object.prototype.hasOwnProperty.call(v, 'full')),
            take(1),
            timeout(4000),
            catchError(() => of(u))
          );
        })
      )
    ).catch(() => void 0);
  };
}


export function authInitializer(cfg: ConfigService, http: HttpClient, splashscreen: SplashscreenService) {
  return async () => {
    // Ensure runtime config is fully loaded before we touch any endpoints
    await cfg.whenReady();

    const splashConfig = cfg.splashscreen();
    const useNewDashboard = !!cfg.dashboard().enableNewDashboard;
    splashscreen.configure(splashConfig, useNewDashboard);
    splashscreen.startAuthCheck();

    const bffUser = cfg.bffUserEndpoint();
    const bffLogin = cfg.bffLoginUrl();

    try {
      // Establish session / detect authenticated user; cookie-based, so withCredentials true
      await lastValueFrom(
        http.get(bffUser, { withCredentials: true })
      );
      splashscreen.startDashboardInit();
    } catch (err: any) {
      // If unauthenticated, redirect immediately to login
      if (err?.status === 401) {
        window.location.replace(bffLogin);
      }
      // Allow Angular bootstrap to continue for unauthenticated users
      return;
    }
  };
}




// Example of how to  set up  a lazy-loaded module
// Create a "future state" (a placeholder) for the Contacts
// Angular module which will be lazy loaded by UI-Router
export const contactsFutureState = {
  name: 'contacts.**',
  url: '/contacts',
  loadChildren: () => import('./contacts/contacts.module').then(m => m.ContactsModule),
};


// having Reports as a Lazy-loaded module prevents 'ManagePdfReports' from being
// activated until the reports module is loaded.It is an AngularJS module so is
// unable to act as the activator
// Unhooke lazy loading for this module 26 July2022 (may bring it back later)
export const reportsFutureState = {
  name: 'reports.**',
  url: '/reports',
  loadChildren: () => import('./reports/reports.module').then(m => m.ReportsModule),
};

export const userManagerFutureState = {
  name: 'userManager.**',
  url: '/usermanager',
  loadChildren: () => import('./user-manager/user-manager.module').then(m => m.UserManagerModule),
};

export const apiKeyManagerFutureState = {
  name: 'keyManager.**',
  url: '/keymanager',
  loadChildren: () => import('./api-key-manager/api-key-manager.module').then(m => m.ApiKeyManagerModule),
};

export const apiSandboxFutureState = {
  name: 'apiSandbox.**',
  url: '/sandboxapi',
  loadChildren: () => import('./api-sandbox/api-sandbox.module').then(m => m.ApiSandboxModule),
};

export const assetMarkerEditorFutureState = {
  name: 'assetmarkereditor.**',
  url: '/assetmarkereditor',
  loadChildren: () => import('./asset-marker-editor/asset-marker-editor.module').then(m => m.AssetMarkerEditorModule),
};

export const dataCentreEditorFutureState = {
  name: 'datacentreeditor.**',
  url: '/datacentreeditor',
  loadChildren: () => import('./data-centre-editor/data-centre-editor.module').then(m => m.DataCentreEditorModule),
};

export const informationRetrievalAgentFutureState = {
  name: 'ego.**',
  url: '/ego',
  loadChildren: () => import('./information-retrieval-agent/information-retrieval-agent.module').then(m => m.InformationRetrievalAgentModule),
};

export const helpFutureState = {
  name: 'help.**',
  url: '/help',
  loadChildren: () => import('./help/help.module').then(m => m.HelpModule),
};

export const userAccountFutureState = {
  name: 'userAccount.**',
  url: '/useraccount',
  loadChildren: () => import('./user-account/user-account.module').then(m => m.UserAccountModule),
};

export const assetsFutureState = {
  name: 'assets.**',
  url: '/assets',
  loadChildren: () => import('./assets/assets.module').then(m => m.AssetsModule),
};

function assertNg1ModuleExists(name: string): void {
  try { angular.module(name); }
  catch (e) {
    console.error(`AngularJS module "${name}" not found. Check script order and exports.`);
    throw e;
  }
}

@NgModule({
  declarations: [
    FunctionUnavailableComponent
  ],
  imports: [
    CommonModule,
    BrowserModule,
    UpgradeModule,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    // Provide angular upgrade capabilities
    UIRouterUpgradeModule,
    // Provides the @uirouter/angular directives and registers
    // the future state for the lazy loaded contacts module reportsFutureState,
    UIRouterModule.forChild({
      states: [
        contactsFutureState,
        reportsFutureState,
        userManagerFutureState,
        apiKeyManagerFutureState,
        apiSandboxFutureState,
        helpFutureState,
        userAccountFutureState,
        assetsFutureState,
        assetMarkerEditorFutureState,
        dataCentreEditorFutureState,
        informationRetrievalAgentFutureState
      ]
    }),
    DsCoreModule,
    // The preferences feature module
    // Example of an Angular module interacting with AngularJs
    PrefsModule,
    UserGuideModule,
    BrowserAnimationsModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatExpansionModule,
    MatListModule,
    MatSelectModule,
    MatSlideToggleModule,
    MenuModule,
    DashboardModule,
    ProjectDisplayModule,
    NotificationsModule,
    GeographicalSummaryModule,
    AssetAnalysisChartModule,
    NgbModule
  ],
  providers: [
    CurrentUserService,
    EventQueueService,
    LastSearchService,

    AppInitService,

    ...provideConfigSlices(),
    // provideProjectStatesInitializer(),

    // 1) CONFIG must be registered as an APP_INITIALIZER
    {
      provide: APP_INITIALIZER,
      useFactory: configInitializer,
      deps: [ConfigService],
      multi: true
    },
    // 2) EICDataStream Init must be registered as an APP_INITIALIZER
    {
      provide: APP_INITIALIZER,
      useFactory: initializeEICDataStream,
      deps: [AppInitService],
      multi: true
    },


    // Add this initializer for authentication setup
    // to ensure deterministic call to /bff/user to ensure 
    // early authentication
    // AUTH must depend on ConfigService and await cfg.whenReady()
    {
      provide: APP_INITIALIZER,
      useFactory: authInitializer,
      deps: [ConfigService, HttpClient, SplashscreenService],
      multi: true
    },

    // ADD this initializer for the user service
    {
      provide: APP_INITIALIZER,
      useFactory: initCurrentUserFactory,
      deps: [CurrentUserService, ConfigService, SplashscreenService],
      multi: true
    },

    // Provide the SystemJsNgModuleLoader when using Angular lazy loading
    // this is deprecated and I don;t know what  replaces it.
    // currently  I  am not lazy loading any  modules
    //  { provide: NgModuleFactoryLoader, useClass: SystemJsNgModuleLoader },
    // Register some AngularJS services as Angular providers
    // { provide: 'DialogService', deps: ['$injector'], useFactory: getDialogService },
    // { provide: 'Contacts', deps: ['$injector'], useFactory: getContactsService },
    {
      provide: 'SearchService',
      useFactory: ($injector: any) => $injector.get('searchService'),
      deps: ['$injector']
    },

    // replace the CurrentUser service with the Angular version
    // {
    //   provide: 'CurrentUser',
    //   useFactory: ($injector: any) => $injector.get('CurrentUser'),
    //   deps: ['$injector']
    // },
    { provide: HTTP_INTERCEPTORS, useClass: BffInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    //provideHttpClient(withInterceptorsFromDi()),
    ThemeService,
    FeaturesService
  ]
})
export class AppModule implements DoBootstrap {
  constructor(
    private upgrade: UpgradeModule,
    private injector: Injector,
    appIconRegistry: AppIconRegistryService
  ) {
    void appIconRegistry;
  }

  async ngDoBootstrap(): Promise<void> {
    // Ensure Angular initialisers are done (they already are at this point),
    // but if you want absolute clarity you may await cfg.whenReady() here.
    const cfg = this.injector.get(ConfigService);
    await cfg.whenReady();

    const themeService = this.injector.get(ThemeService);
    themeService.applyConfiguredTheme();


    assertNg1ModuleExists(EICDataStream3Config.name);
    assertNg1ModuleExists(EICDataStream3Module.name);

    // Apply the runtime config to the AngularJS configuration module
    applyAngularJsConfigFromRuntime(cfg);


    // 3) Register the downgraded Angular service on an AngularJS module
    //    You may choose the *root* legacy module (e.g., 'EICDataStream3') or a shared core module.
    //    Registering on the root module makes it available everywhere.
    angular
      .module(EICDataStream3Module.name)                 // replace with your real root AngularJS module
      .factory('configService', downgradeInjectable(ConfigService))
      .factory('managePdfReportRequestLauncherService', downgradeInjectable(ManagePdfReportRequestLauncherService));


    // Now bootstrap AngularJS. All constants/values are registered and ready.
    this.upgrade.bootstrap(document.body, [EICDataStream3Module.name], { strictDi: false });


    // ✅ Now it is safe to register states that include AngularJS controllers/templates
    const uiRouter = this.injector.get(UIRouter);
    const registry = uiRouter.stateRegistry;

    const features = this.injector.get(FEATURES_TOKEN as any) as ReturnType<ConfigService['features']>;

    registerProjectStates(registry, {
      useNewProjectDisplay: !!features.useNewProjectDisplay,
      enableAssetDisplay: !!features.enableAssetDisplay
    });

    registerNotificationStates(registry, {
      useNewNotifications: !!(features.notifications?.useNewNotifications ?? features.useNewNotifications)
    });

    const geographicalSummary = cfg.geographicalSummary();
    if (geographicalSummary?.enableNewGeographicalSummary) {
      const existingState = registry.get('geographicalSummary') as any;
      const isLegacyGeographicalSummaryState = !!existingState?.templateUrl;
      if (isLegacyGeographicalSummaryState) {
        registry.deregister('geographicalSummary');
      }
    }

    registerGeographicalSummaryStates(registry, {
      enableNewGeographicalSummary: !!geographicalSummary.enableNewGeographicalSummary
    });
  }
}


